import * as React from 'react'
import { graphql } from 'gatsby'
import { SectionValuesList } from 'components/SectionValuesList/SectionValuesList'
import { CategoriesSection } from '../components/CategoriesSection/CategoriesSection'
import { SectionWithTitle } from '../components/SectionWithTitle/SectionWithTitle'
import { AppBanner } from '../components/Banner/AppBanner'
import { BrandCarousel } from '../components/BrandCarousel/BrandCarousel'
import { DeliveryLocations } from '../components/DeliveryLocations/DeliveryLocations'
import { HomePageHero } from 'components/Hero/HomePageHero'
import { PromoDisclaimerSection } from 'components/PromoDisclaimerSection/PromoDisclaimerSection'
import { LocalFavourites } from 'components/LocalFavourites/LocalFavourites'
import { SEO } from 'components/SEO/SEO'
import {
  ContentfulMainQuery,
  ContentfulLocationPageQuery,
} from '../../graphql/generated-types'
import {
  mapContentfulBrandLogos,
  mapContentfulCategoriesToCategoriesTileData,
  mapContentfulStoreIconsToColorsSet,
  mapContentfulValues,
} from 'utils/helpers'
import { SharedPageContext } from './types'

export type LocationPageTemplateProps = {
  pageContext: SharedPageContext & {
    categoriesData: ContentfulMainQuery['categoriesData']['categoriesSection']['data']
  }
  data: ContentfulLocationPageQuery
}

const LocationPageTemplate = ({
  pageContext: {
    sharedPageData,
    categoriesData,
    prefixes: { categoryPrefix },
  },
  data,
}: LocationPageTemplateProps) => {
  const {
    heroCarouselImages,
    categoriesSection,
    storesIcons,
    appBannerSection,
    asSeenInSection,
    valuesListSection,
    localFavouritesSectionTitle,
    isPromoActive,
    promotion,
  } = sharedPageData
  const { copy, seo, heroCopy, deliveryLocations, localFavourites } =
    data.contentfulLocation
  const storesIconsMapped = mapContentfulStoreIconsToColorsSet(storesIcons)

  return (
    <>
      <SEO title={seo.title} description={seo.description.description} />
      <HomePageHero title={heroCopy} images={heroCarouselImages} />
      <SectionValuesList
        values={mapContentfulValues(valuesListSection.values)}
      />
      {localFavourites && localFavouritesSectionTitle && (
        <LocalFavourites
          items={localFavourites}
          title={localFavouritesSectionTitle}
        />
      )}

      <CategoriesSection
        title={categoriesSection.title}
        categories={mapContentfulCategoriesToCategoriesTileData(categoriesData)}
        pathnamePrefix={categoryPrefix}
      />
      <SectionWithTitle title={copy.title} textContent={copy.text} />
      {deliveryLocations ? (
        <DeliveryLocations
          title={deliveryLocations.title}
          locations={deliveryLocations.postCodes}
        />
      ) : null}
      {asSeenInSection && (
        <SectionWithTitle title={asSeenInSection.title}>
          <BrandCarousel
            icons={mapContentfulBrandLogos(asSeenInSection.brandLogos)}
          />
        </SectionWithTitle>
      )}

      {isPromoActive && promotion ? (
        <PromoDisclaimerSection text={promotion.disclaimer} />
      ) : null}
      <AppBanner
        image={appBannerSection.image}
        storesIcons={storesIconsMapped.black}
        text={appBannerSection.desktop}
        mobileText={appBannerSection.desktop}
      />
    </>
  )
}

export const query = graphql`
  query ContentfulLocationPage($id: String!) {
    contentfulLocation(id: { eq: $id }) {
      name
      copy {
        title
        text {
          raw
        }
        disclaimers
      }
      deliveryLocations {
        title
        postCodes
      }
      heroCopy
      localFavourites {
        id
        name
        description
        url
        image {
          alternativeText
          image {
            gatsbyImageData
          }
        }
      }
      seo {
        title
        description {
          description
        }
      }
    }
  }
`

export default LocationPageTemplate
